import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// 24‑hour TTL for stored scroll positions
const SCROLL_TTL = 24 * 60 * 60 * 1000;

const storageKey = (pathname) => `scroll_position_${pathname}`;

// Save current scroll position for a pathname
const savePosition = (pathname) => {
    const data = {
        y: window.scrollY,
        ts: Date.now(),
    };
    try {
        sessionStorage.setItem(storageKey(pathname), JSON.stringify(data));
    } catch (e) {
        console.error('[ScrollManager] save error', e);
    }
};

// Load saved scroll position for a pathname (null if none/expired)
const loadPosition = (pathname) => {
    try {
        const raw = sessionStorage.getItem(storageKey(pathname));
        if (!raw) return null;
        const { y, ts } = JSON.parse(raw);
        if (Date.now() - ts > SCROLL_TTL) {
            sessionStorage.removeItem(storageKey(pathname));
            return null;
        }
        return y;
    } catch (e) {
        console.error('[ScrollManager] load error', e);
        return null;
    }
};

/**
 * ScrollManager component – does not render UI.
 * It disables native scroll restoration, saves scroll on pathname change/unmount,
 * and restores scroll after navigation, retrying until the document height
 * stabilises (handles async data loading).
 */
const ScrollManager = () => {
    const { pathname } = useLocation();
    const prevPathRef = useRef(null); // Initialize as null to detect first load
    const retryRef = useRef(0);
    const maxRetries = 50; // Increased retries for slower connections
    const retryDelay = 50; // Faster initial check
    const lastScrollY = useRef(0);
    const lastDocHeight = useRef(0); // Track document height changes

    // Disable native browser scroll restoration
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        return () => {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);

    // Save scroll position on scroll (debounced)
    useEffect(() => {
        let timeoutId;

        const handleScroll = () => {
            const currentY = window.scrollY;
            const delta = Math.abs(currentY - lastScrollY.current);

            // Update tracker
            lastScrollY.current = currentY;

            // Heuristic: If sudden jump to 0 (likely navigation reset), ignore
            if (currentY === 0 && delta > 50) {
                return;
            }

            if (pathname) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => savePosition(pathname), 100);
            }
        };

        window.addEventListener('scroll', handleScroll);

        // Save on beforeunload (refresh/close) to capture the exact state
        const handleBeforeUnload = () => {
            savePosition(pathname);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearTimeout(timeoutId);
        };
    }, [pathname]);

    // Restore scroll after navigation
    useLayoutEffect(() => {
        // If it's the same path (and not null), don't restore
        if (prevPathRef.current === pathname) return;

        const targetY = loadPosition(pathname);
        const scrollToY = (y) => {
            window.scrollTo({ top: y, behavior: 'instant' });
        };

        // Initial attempt
        const desiredY = targetY != null ? targetY : 0;
        scrollToY(desiredY);
        lastDocHeight.current = document.documentElement.scrollHeight;

        const checkAndRetry = () => {
            const currentY = window.scrollY;
            const docHeight = document.documentElement.scrollHeight;
            const viewHeight = window.innerHeight;
            const maxScroll = Math.max(0, docHeight - viewHeight);

            // If we are close enough to the target, stop
            if (Math.abs(currentY - desiredY) <= 10) {
                retryRef.current = 0;
                return;
            }

            // If document height grew, reset retries to give more time
            if (docHeight > lastDocHeight.current) {
                retryRef.current = 0;
                lastDocHeight.current = docHeight;
            }

            // If we hit the bottom but wanted to go further, keep retrying in case content grows
            // OR if we haven't reached the target yet
            if (retryRef.current < maxRetries) {
                retryRef.current += 1;

                // If we are already at max scroll but need to go further, it means content is loading.
                // If we are NOT at max scroll but not at target, we might be fighting another scroll event or layout shift.
                // In both cases, force scroll again.
                const newFinal = Math.min(desiredY, maxScroll);
                if (newFinal !== currentY) {
                    scrollToY(newFinal);
                }

                // Gentler backoff: 50ms * 1.05^n. Cap at 200ms.
                const nextDelay = Math.min(200, retryDelay * Math.pow(1.05, retryRef.current));
                setTimeout(checkAndRetry, nextDelay);
            } else {
                retryRef.current = 0;
            }
        };

        // Start checking
        retryRef.current = 0;
        setTimeout(checkAndRetry, retryDelay);

        prevPathRef.current = pathname;
    }, [pathname]);

    return null;
};

export default ScrollManager;
